import { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import {
  Plane, Thermometer, User, Plus, CheckCircle2, XCircle, Hourglass, X, Ban,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";

const TYPE_META = {
  vacation: { label: "Vacation", icon: Plane, color: "text-sky-600", bg: "bg-sky-50", border: "border-sky-100" },
  sick:     { label: "Sick",     icon: Thermometer, color: "text-rose-600", bg: "bg-rose-50", border: "border-rose-100" },
  personal: { label: "Personal", icon: User, color: "text-violet-600", bg: "bg-violet-50", border: "border-violet-100" },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtRange = (start, end) => {
  const s = new Date(start + "T00:00:00");
  const e = new Date(end + "T00:00:00");
  const opts = { month: "short", day: "numeric" };
  if (s.toDateString() === e.toDateString()) {
    return s.toLocaleDateString([], { ...opts, year: "numeric" });
  }
  const yrSame = s.getFullYear() === e.getFullYear();
  return `${s.toLocaleDateString([], opts)} – ${e.toLocaleDateString([], { ...opts, year: yrSame ? undefined : "numeric" })}${yrSame ? `, ${s.getFullYear()}` : ""}`;
};

/**
 * Staff-facing "Request Time Off" widget.
 * Sits on the Employee Portal / StylistDay page. Staff submit vacation,
 * sick, or personal day requests; yearly totals of approved days appear
 * at the top so they always know what they've taken.
 */
export default function TimeOffCard() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ type: "vacation", start_date: "", end_date: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        api.get("/time-off/my?limit=10"),
        api.get("/time-off/my/summary"),
      ]);
      setList(l.data || []);
      setSummary(s.data || null);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openDialog = () => {
    const t = todayISO();
    setForm({ type: "vacation", start_date: t, end_date: t, reason: "" });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.start_date || !form.end_date) {
      toast.error("Start and end date required"); return;
    }
    if (form.end_date < form.start_date) {
      toast.error("End date must be on or after start date"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/time-off", {
        type: form.type,
        start_date: form.start_date,
        end_date: form.end_date,
        reason: form.reason.trim(),
      });
      toast.success("Time-off request submitted");
      setOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    }
    setSubmitting(false);
  };

  const cancelRequest = async (req) => {
    if (!window.confirm("Cancel this request?")) return;
    try {
      await api.post(`/time-off/${req.request_id}/cancel`);
      toast.success("Request cancelled");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const calcDays = (start, end) => {
    const s = new Date(start + "T00:00:00").getTime();
    const e = new Date(end + "T00:00:00").getTime();
    return Math.round((e - s) / 86400000) + 1;
  };

  const pending = list.filter(r => r.status === "pending");
  const recent = list.filter(r => r.status !== "pending").slice(0, 3);

  const MiniStat = ({ type, value }) => {
    const m = TYPE_META[type];
    return (
      <div className={`${m.bg} ${m.border} border rounded-xl px-2.5 py-2`} data-testid={`to-stat-${type}`}>
        <div className="flex items-center gap-1.5">
          <m.icon className={`w-3 h-3 ${m.color}`} strokeWidth={2} />
          <span className={`text-[9px] font-bold uppercase tracking-wider ${m.color}`}>{m.label}</span>
        </div>
        <p className="text-lg font-semibold text-slate-900 font-['Outfit'] tabular-nums mt-0.5">{value}<span className="text-xs text-slate-400 font-normal ml-1">day{value === 1 ? "" : "s"}</span></p>
      </div>
    );
  };

  return (
    <>
      <div data-testid="time-off-card" className="bg-white border border-slate-200 rounded-2xl p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Plane className="w-4 h-4 text-slate-500" />
            <h3 className="font-['Outfit'] text-base font-medium text-slate-900">Time Off</h3>
          </div>
          <Button
            size="sm"
            onClick={openDialog}
            data-testid="request-time-off-btn"
            className="bg-slate-900 text-white hover:bg-slate-800 rounded-full h-8 px-4 text-xs">
            <Plus className="w-3.5 h-3.5 mr-1" /> Request
          </Button>
        </div>

        {summary && (
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid="time-off-summary">
            <MiniStat type="vacation" value={summary.vacation || 0} />
            <MiniStat type="sick" value={summary.sick || 0} />
            <MiniStat type="personal" value={summary.personal || 0} />
          </div>
        )}
        {summary && (
          <p className="text-[10px] text-slate-400 font-['Manrope'] mb-4 text-right">
            Approved days taken in {summary.year}
          </p>
        )}

        {loading ? (
          <div className="h-5 w-32 bg-slate-100 rounded animate-pulse" />
        ) : list.length === 0 ? (
          <p className="text-xs text-slate-400 font-['Manrope']">No time-off requests yet.</p>
        ) : (
          <div className="space-y-2">
            {pending.length > 0 && (
              <div data-testid="to-pending-section">
                <p className="text-[10px] uppercase tracking-widest text-amber-600 font-bold mb-1.5 font-['Manrope']">Awaiting review</p>
                {pending.map(r => {
                  const m = TYPE_META[r.type] || TYPE_META.personal;
                  return (
                    <div key={r.request_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`to-my-req-${r.request_id}`}>
                      <Hourglass className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                      <m.icon className={`w-3 h-3 ${m.color} shrink-0`} />
                      <span className="text-slate-700 flex-1">{fmtRange(r.start_date, r.end_date)} · <span className="text-slate-400">{r.total_days}d {m.label.toLowerCase()}</span></span>
                      <button
                        onClick={() => cancelRequest(r)}
                        data-testid={`to-cancel-${r.request_id}`}
                        title="Cancel request"
                        className="text-slate-300 hover:text-rose-500 transition-colors p-1">
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {recent.length > 0 && (
              <div>
                <p className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-1.5 mt-2 font-['Manrope']">Recent</p>
                {recent.map(r => {
                  const m = TYPE_META[r.type] || TYPE_META.personal;
                  const Icon = r.status === "approved" ? CheckCircle2 : r.status === "rejected" ? XCircle : Ban;
                  const iconCls = r.status === "approved" ? "text-emerald-500"
                    : r.status === "rejected" ? "text-rose-500"
                    : "text-slate-400";
                  const pillCls = r.status === "approved"
                    ? "text-emerald-600 bg-emerald-50 border border-emerald-100"
                    : r.status === "rejected"
                    ? "text-rose-600 bg-rose-50 border border-rose-100"
                    : "text-slate-500 bg-slate-100 border border-slate-200";
                  return (
                    <div key={r.request_id} className="flex items-center gap-2 py-1.5 text-xs" data-testid={`to-my-req-${r.request_id}`}>
                      <Icon className={`w-3.5 h-3.5 shrink-0 ${iconCls}`} />
                      <m.icon className={`w-3 h-3 ${m.color} shrink-0`} />
                      <span className="text-slate-700 flex-1">{fmtRange(r.start_date, r.end_date)} · <span className="text-slate-400">{r.total_days}d {m.label.toLowerCase()}</span></span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded capitalize ${pillCls}`}>{r.status}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px] bg-white rounded-2xl p-8">
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-xl font-medium">Request Time Off</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs text-slate-500 font-['Manrope'] mb-1.5 block">Type *</label>
              <div className="grid grid-cols-3 gap-2">
                {Object.entries(TYPE_META).map(([key, m]) => {
                  const active = form.type === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, type: key }))}
                      data-testid={`to-type-${key}`}
                      className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border transition-all ${
                        active ? `${m.bg} ${m.border} ${m.color} font-semibold`
                          : "bg-white border-slate-200 text-slate-500 hover:bg-slate-50"
                      }`}>
                      <m.icon className="w-4 h-4" strokeWidth={active ? 2.2 : 1.8} />
                      <span className="text-xs">{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Start date *</label>
                <Input type="date" value={form.start_date}
                  onChange={e => setForm(f => ({ ...f, start_date: e.target.value, end_date: f.end_date < e.target.value ? e.target.value : f.end_date }))}
                  data-testid="to-start"
                  className="h-10 border-slate-200 rounded-lg" />
              </div>
              <div>
                <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">End date *</label>
                <Input type="date" value={form.end_date} min={form.start_date}
                  onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))}
                  data-testid="to-end"
                  className="h-10 border-slate-200 rounded-lg" />
              </div>
            </div>
            {form.start_date && form.end_date && form.end_date >= form.start_date && (
              <p className="text-xs text-slate-500 font-['Manrope']">
                That's <span className="font-semibold text-slate-800">{calcDays(form.start_date, form.end_date)} day{calcDays(form.start_date, form.end_date) === 1 ? "" : "s"}</span> of {TYPE_META[form.type].label.toLowerCase()} time
              </p>
            )}
            <div>
              <label className="text-xs text-slate-500 font-['Manrope'] mb-1 block">Reason (optional)</label>
              <textarea
                rows={3}
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value.slice(0, 500) }))}
                placeholder="e.g. family trip to Italy, doctor's appointment…"
                data-testid="to-reason"
                className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none outline-none focus:border-rose-300" />
              <p className="text-[10px] text-slate-400 mt-0.5 text-right">{form.reason.length}/500</p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="to-cancel-btn"
                className="flex-1 border-slate-200 rounded-full h-11">
                Cancel
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="to-submit"
                className="flex-1 bg-slate-900 text-white hover:bg-slate-800 rounded-full h-11">
                {submitting ? "Sending…" : "Submit Request"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
